import { useForm } from "react-hook-form";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { useEffect, useState } from "react";
import axios from "../../MyAxios";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { EDIT_PROFILE, PROFILE } from "../../constants/api";
import { useNavigate } from "react-router-dom";
import Button from "../../components/Button";
import Layout from "../../Layout";
import Title from "../../components/Title";
import useLoggedIn from "../../hooks/useLoggedIn";
import { refreshUserRecord } from "@helpers";
import SignupAutocomplete from "@components/SignupAutocomplete";

const useStyles = makeStyles((theme) => ({
  border: {
    borderWidth: 1,
    borderColor: "#887E7E",
  },
  textColor: {
    color: theme.palette.primary.main,
  },
  button: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },
  fontSmall: {
    fontSize: "10px",
  },
  bottomImg: {
    position: "fixed",
    bottom: 0,
    right: 0,
    left: 0,
  },
}));

const EditProfile = (res) => {
  useLoggedIn();
  const classes = useStyles();
  const navigation = useNavigate();
  const { userRecord } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm();
  
  const [totalCountries, setTotalCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    setValue("userName", userRecord.userName);
    setValue("id", userRecord.id);
    setValue("emailAddress", userRecord.email || "");
    setValue("phoneNumber", userRecord.phoneNumber || "");
    setValue("firstName", userRecord.firstName || "");
    setValue("lastName", userRecord.lastName || "");
    setValue("aliasOne", userRecord.aliasOne || "");
    setValue("aliasTwo", userRecord.aliasTwo || "");
    setValue("about", userRecord.about || "");
    setValue("country", userRecord.country || null);
    setValue("state", userRecord.state || null);
    setValue("city", userRecord.city || null);
    setValue("timezone", userRecord.timezone || "");

    getGeoData();
  }, [userRecord, setValue]);
  
  const getGeoData = () => {
    axios.get('/user/get-geo-data', {
      params: {
        timezone: userRecord.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    })
      .then(res => {
        if(res.data?.error) {
          console.log(res.data);
          return;
        }

        if(res.data.allCountries)
          setTotalCountries(res.data.allCountries);

        // if(res.data.autoSelectedCountry && !watch('country'))
        //   setValue('country', res.data.autoSelectedCountry);
      })
      .catch(err => {
        console.log(err);
      })
  }

  const getGeoStates = () => {
    if(typeof watch('country') === 'string')
      return;

    axios.get('/user/get-country-geo-states', {
      params: {
        country: watch('country')?.name
      }
    })
      .then(res => {
        if(res.data?.error) {
          console.log(res.data);
          return;
        }

        if(res.data.states)
          setStates(res.data.states);
      })
      .catch(err => {
        console.log(err);
      })
  }

  const getGeoCities = () => {
    if(typeof watch('state') === 'string')
      return;

    axios.get('/user/get-state-geo-cities', {
      params: {
        stateId: watch('state')?._id
      }
    })
      .then(res => {
        if(res.data?.error) {
          console.log(res.data);
          return;
        }

        if(res.data.cities)
          setCities(res.data.cities);
      })
      .catch(err => {
        console.log(err);
      })
  }

  // Form submission handler
  const onSubmit = async (data) => {
    if(!data.country)
      return setError('country', { message: 'Please select a country' });

    if(!data.state)
      return setError('state', { message: 'Please select a state' });


    data.country = data.country.name;
    data.state = data.state.name;
    data.city = data.city?.name || '';

    try {
      await axios.post(EDIT_PROFILE, data).then((res) => {
        refreshUserRecord(dispatch);
        navigation(`/${userRecord?.userName}`);
      });
    } catch (error) {}
  };

  const validatePhoneNumber = (value) => {
    if (value === "") return true;
    const phoneNumberDigits = value.replace(/\D/g, ""); // Remove non-digits
    if (!/^\d+$/.test(phoneNumberDigits)) {
      // Check if contains only digits
      return "Phone number must contain only digits";
    }
    if (phoneNumberDigits.length !== 10) {
      // Check if length is 10
      return "Phone number must be 10 digits";
    }
    return true; // Validation passed
  };

  useEffect(() => {
    if(totalCountries?.length && watch('country') && typeof watch('country') === 'string') {
      const selectedCountry = totalCountries.find(country => country.name === watch('country'));
      setValue('country', selectedCountry);
    }

    if(states?.length && watch('state') && typeof watch('state') === 'string') {
      const selectedState = states.find(state => state.name === watch('state'));
      setValue('state', selectedState);
    }

    if(cities?.length && watch('city') && typeof watch('city') === 'string') {
      const selectedCity = cities.find(city => city.name === watch('city'));
      setValue('city', selectedCity);
    }

  }, [totalCountries, states, cities, watch('country'), watch('state'), watch('city')]);

  useEffect(() => {
    if(watch('country')) {
      getGeoStates();
      clearErrors('country');
    }

    if(typeof watch('state') !== 'string') {
      setValue('state', null);
      setStates([]);
    }
  }, [watch('country')]);

  useEffect(() => {
    if(watch('state')) {
      getGeoCities();
      clearErrors('state');
    }

    if(typeof watch('city') !== 'string') {
      setValue('city', null);
      setCities([]);
    }
  }, [watch('state')]);

  useEffect(() => {
    if(watch('city')) {
      clearErrors('city');
    }

  }, [watch('city')]);

  return (
    <Layout>
      <Title back={`${userRecord?.userName}`}>Edit Profile</Title>
      <form
        onSubmit={handleSubmit(onSubmit)}
        encType="multipart/form-data"
        className="mx-10 mt-4 flex flex-col gap-2"
      >
        <div className="input-wrapper flex flex-col">
          <label className="ml-1">
            <small>First Name</small>
          </label>
          <input
            className={clsx(classes.border, "rounded-md p-3")}
            placeholder="First Name"
            type="text"
            {...register("firstName", {
              required: "First Name is required",
              minLength: {
                value: 3,
                message: "First Name must be at least 3 characters",
              },
            })}
          />
          {errors.firstName && (
            <p className="text-xs italic text-red-500">
              {errors.firstName.message}
            </p>
          )}
        </div>

        <div className="input-wrapper flex flex-col">
          <label className="ml-1">
            <small>Last Name</small>
          </label>
          <input
            className={clsx(classes.border, "rounded-md p-3")}
            placeholder="Last Name"
            type="text"
            {...register("lastName", {
              required: "Last Name is required",
              minLength: {
                value: 3,
                message: "Last Name must be at least 3 characters",
              },
            })}
          />
          {errors.lastName && (
            <p className="text-xs italic text-red-500">
              {errors.lastName.message}
            </p>
          )}
        </div>

        <div className="input-wrapper flex flex-col">
          <label className="ml-1">
            <small>Phone Number</small>
          </label>
          <input
            className={clsx(classes.border, "rounded-md p-3")}
            placeholder="(XXX) XXX-XXXX"
            type="text"
            {...register("phoneNumber", {
              validate: validatePhoneNumber,
              required: false,
            })}
          />
          {errors.phoneNumber && (
            <p className="text-xs italic text-red-500">
              {errors.phoneNumber.message}
            </p>
          )}
        </div>

        <div className="input-wrapper flex flex-col">
          <label className="ml-1">
            <small>Country *</small>
          </label>
          <SignupAutocomplete
            options={totalCountries || []}
            getOptionLabel={(option) => option.name}
            name = 'country'
            value={watch("country") ?? null}
            onChange={setValue}
          />
          {errors.country && (
            <p className="text-xs italic text-red-500">{errors.country.message}</p>
          )}
        </div>

        <div className="input-wrapper flex flex-col">
          <label className="ml-1">
            <small>State/Region *</small>
          </label>
          <SignupAutocomplete
            options={states || []}
            getOptionLabel={(option) => option.name}
            name = 'state'
            value={watch("state") ?? null}
            onChange={setValue}
          />
          {errors.state && (
            <p className="text-xs italic text-red-500">{errors.state.message}</p>
          )}
        </div>
        <div className="input-wrapper flex flex-col">
          <label className="ml-1">
            <small>City *</small>
          </label>
          <SignupAutocomplete
            options={cities || []}
            getOptionLabel={(option) => option.name}
            name = 'city'
            value={watch("city") ?? null}
            onChange={setValue}
          />
          {errors.city && (
            <p className="text-xs italic text-red-500">{errors.city.message}</p>
          )}
        </div>

        <div className="input-wrapper flex flex-col">
          <label className="ml-1">
            <small>About Me</small>
          </label>
          <textarea
            className={clsx(classes.border, "rounded-md p-3")}
            placeholder="About"
            {...register("about")}
          />
        </div>

        <div className="input-wrapper">
          <Button>Save your profile</Button>
        </div>
      </form>
    </Layout>
  );
};

export default EditProfile;
